import {TypedEventTarget} from '../../logics/TypedEventTarget';
import {
  ETextParamType_OnReceivedStartScreenShare,
  ETextParamType_OnReceivedStopScreenShare,
  ETextParamType_OnStopScreenShare,
  HEL_AUDIO_TYPE_VOICE
} from '../../logics/const';
import {
  getMixerMuteAt,
  getMixerSettingAt,
  getSkywayOnTellScreenShareStart,
  getSkywayOnTellWaitScreenShare,
  getUseSkywayAudioGainToggle,
  getMainAudioContext
} from '../../logics/dataOps';
import {ensureScriptLoaded} from '../../logics/externalScriptLoader';
import {getLocalStream, closeLocalStream, setLocalStream} from '../../logics/localStream';
import {hel_setText} from '../../logics/moduleCaller';
import {hel_isiPhone} from '../../logics/platform';
import {setMicEnabled} from '../../logics/shared';
import {
  hel_video_stop,
  hel_video_close_genericwindow,
  hel_skyway_stop_screen_sharing_via_room,
  hel_video_is_playing_same_stream
} from '../../logics/video_callee';
import {GetIHeliNetwork} from '../../singleton_registry';
import {
  onNetReceiveVoiceJoin,
  onNetReceiveVoiceLeave,
  onNetVoiceConnected,
  onNetVoiceDisconnected
} from '../networkCallback';

import type {
  IHeliVoiceChannelProvider,
  VoiceChannelProviderEventMap
} from './IHeliVoiceChannelProvider';
import type {PositionChannelDetail} from '../../types/network';
import type {IHeliMic, IHeliRtcTokenRoomTarget, Transform} from '../CHeliNetwork';
import type {CHeliPlayer} from '../CHeliPlayer';
import type {PositionChannelProviderEventMap} from '../positionChannelProviders/IHeliPositionChannelProvider';
import type {
  LocalSFURoomMember,
  LocalStream,
  LocalVideoStream,
  LocalAudioStream,
  RoomPublication,
  SfuRoom,
  SkyWayContext,
  SkyWayRoom,
  SkyWayStreamFactory
} from '@skyway-sdk/room';

declare global {
  interface MediaTrackConstraints {
    cursor?: 'always' | 'motion' | 'never';
  }

  /** @see https://github.com/skyway/js-sdk/tree/95d24781242ca5dcdc29cb3f4e820f4fca862118?tab=readme-ov-file#cdn-%E3%82%92%E5%88%A9%E7%94%A8%E3%81%99%E3%82%8B%E5%A0%B4%E5%90%88 */
  // eslint-disable-next-line no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須
  var skyway_room:
    | {
        SkyWayContext: typeof SkyWayContext;
        SkyWayStreamFactory: typeof SkyWayStreamFactory;
        SkyWayRoom: typeof SkyWayRoom;
        LocalAudioStream: typeof LocalAudioStream;
      }
    | undefined;
}

export class CHeliSkywayVoiceChannelProvider
  extends TypedEventTarget<VoiceChannelProviderEventMap>
  implements IHeliVoiceChannelProvider
{
  /** プレイヤーごとの、画面共有の映像において必要となるデータ。 */
  readonly #playerIdScreenShareVideoDataPairs = new Map<
    string,
    {mediaStream: MediaStream; itemName: string; materialName: string}
  >();

  #skywayLocalRoomMember: LocalSFURoomMember | null = null;
  #skywayRoom: SfuRoom | null = null;
  #skywayAudioPublication: RoomPublication<LocalAudioStream> | null = null;
  #skywayVideoPublication: RoomPublication<LocalVideoStream> | null = null;

  readonly #playerIdAudioPairs: Map<
    string,
    {
      readonly element: HTMLAudioElement;
      readonly context: AudioContext | null;
      readonly gainNode: GainNode | null;
      source: MediaStreamAudioSourceNode | null;
      audioDestination: MediaStreamAudioDestinationNode | null;
      attenuateVolume: number;
    }
  > = new Map();

  #globalPlayerVoiceVolume = getMixerSettingAt(HEL_AUDIO_TYPE_VOICE);

  async ensureLibraryLoadedAndInitialized(): Promise<void> {
    await ensureScriptLoaded(
      'https://cdn.jsdelivr.net/npm/@skyway-sdk/room@1/dist/skyway_room-latest.js'
    );
  }

  prepareForNewJoin(): boolean {
    return true;
  }

  onRemoteJoin(): void {}

  onRemoteLeave(detail: PositionChannelProviderEventMap['remoteleave']['detail']): void {
    const {playerId} = detail;
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }
    // skywayRoom.onMemberLeftより前に来る場合もあるので、ここでもボイスチャンネルからの離脱を通知する
    const player = heliNetwork.getPlayer(playerId);
    if (!player) {
      console.error('no player objectId', playerId);
      return;
    }
    // 音声チャンネルからのLeaveを通知
    onNetReceiveVoiceLeave(player.getPlayerId(), '');
  }

  #getVideoData(playerId: string): {
    mediaStream: MediaStream;
    itemName: string;
    materialName: string;
  } {
    const videoData = this.#playerIdScreenShareVideoDataPairs.get(playerId);
    if (videoData) {
      return videoData;
    }

    const newVideoData = {
      mediaStream: new MediaStream(),
      itemName: '',
      materialName: ''
    };
    this.#playerIdScreenShareVideoDataPairs.set(playerId, newVideoData);
    return newVideoData;
  }

  connectVoiceChannel(): void {}

  onData(data: string, playerId: string): void {
    if (data === 'screenshare:tell') {
      const fn = getSkywayOnTellScreenShareStart();
      if (fn !== undefined) {
        fn();
        return;
      }
    }
    if (data === 'screenshare:waitstart') {
      const fn = getSkywayOnTellWaitScreenShare();
      if (fn !== undefined) {
        fn();
        return;
      }
    }
    const paramsScreenShare = data.split(':');
    if (paramsScreenShare[1] === 'start') {
      const videoData = this.#getVideoData(playerId);
      const paramsScreenShare = data.split(':');
      if (paramsScreenShare[2] === undefined || paramsScreenShare[3] === undefined) {
        throw new Error(
          `Invalid screenshare:start format. Expected “screenshare:start:<videoItemName>:<videoItemMaterial>”, but got “${data}”.`
        );
      }
      videoData.itemName = paramsScreenShare[2];
      videoData.materialName = paramsScreenShare[3];
    }
    if (paramsScreenShare[1] === 'stop') {
      // グローバルにhel_onReceiveNoticeScreenShareStopが定義されていた場合に実行する
      if (typeof globalThis.hel_onReceiveNoticeScreenShareStop === 'function') {
        globalThis.hel_onReceiveNoticeScreenShareStop(playerId);
      } else {
        hel_video_stop();

        hel_video_close_genericwindow();

        // HeliScriptのコールバックを呼び出す
        hel_setText(ETextParamType_OnReceivedStopScreenShare, '');
      }
    }
  }

  disconnectFromVoiceChannel(): void {}

  async prepareLeavePositionChannel(): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const positionChannel = heliNetwork.getCurrentPositionChannel();
    if (positionChannel && (!positionChannel.isMultiVc() || positionChannel.isEnableDefaultVc())) {
      heliNetwork.micReady = false;
      if (positionChannel.isEnableDefaultVc()) {
        heliNetwork.setCurrentVoiceChannel(null);
      } else {
        await this.#leaveVoiceRtc();
      }
    }
  }

  async postLeaveVoiceChannelCleanup(): Promise<void> {
    await this.#leaveVoiceRtc();

    // 音声チャンネルからの退出を通知
    onNetVoiceDisconnected();
  }

  async #leaveVoiceRtc() {
    if (this.#skywayLocalRoomMember) {
      hel_skyway_stop_screen_sharing_via_room();

      await this.#skywayLocalRoomMember.leave();
      this.#skywayLocalRoomMember = null;

      if (!this.#skywayRoom) {
        throw new Error('skywayRoom is null');
      }
      if (this.#skywayRoom.members.length === 0) {
        await this.#skywayRoom.dispose();
      }
    }
  }

  async createGenericRtcTokenAndJoinVoiceChannel(
    pIHeliMic: IHeliMic | null,
    pIHeliRtcTokenRoomTarget: IHeliRtcTokenRoomTarget | null
  ): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const currentPosCh = heliNetwork.getCurrentPositionChannel();

    // 座標チャンネルに接続済みか判定
    if (!currentPosCh) {
      throw new Error('getCurrentPositionChannel() returns null.');
    }

    // crp1.2になったらjoinでuserが取れたらuserCodeで
    // const decodeJwt = await this.getDecodedChannelSession()
    // const userCode = decodeJwt.user_code
    const channelSessionJwt = (
      await globalThis.heliport.v3.api.channelSessionApi.getChannelSessionJwt()
    ).data;
    if (!channelSessionJwt) {
      throw new Error('channelSessionApi.getChannelSessionJwt() returns null.');
    }
    const returnValue = await globalThis.heliport.v3.api.genericRtcToken.getToken({
      channelSessionJwt,
      type: 'skyway',
      mode: 'talk',
      disableRtcUser: false,
      // undefinedの場合 voice_channel_code で接続JWT取得
      roomTarget: pIHeliRtcTokenRoomTarget ?? undefined
    });
    if (returnValue.status !== 'ok') {
      throw new Error(`genericRtcToken.getToken() failed: ${JSON.stringify(returnValue, null, 2)}`);
    }
    const token = returnValue.data;
    const skywayToken = token.token;
    const skywayAppId = token.app_id;
    const skywayChannelId = token.room_id;
    const skywayUserId = token.user_id;
    const micEnabled = false;

    if (!globalThis.skyway_room) {
      throw new Error('skyway_room is not defined');
    }
    const {SkyWayContext, SkyWayRoom} = globalThis.skyway_room;
    await heliNetwork.checkTrackState(pIHeliMic?.deviceId ?? null);

    const skywayContext = await SkyWayContext.Create(skywayToken);
    // Expire直前に発火するイベントにてトークンの更新
    skywayContext.onTokenUpdateReminder.add(() => {
      skywayContext.updateAuthToken(skywayToken);
    });

    if (this.#skywayRoom) {
      this.#skywayRoom.onStreamPublished.removeAllListeners();
      this.#skywayRoom.onMemberJoined.removeAllListeners();
      this.#skywayRoom.onMemberLeft.removeAllListeners();
      this.#skywayRoom.onPublicationSubscribed.removeAllListeners();
    }

    // 他人のストリームを再生
    const subscribeAndAttach = async (publication: RoomPublication<LocalStream>) => {
      if (publication.publisher.name === skywayUserId) return;
      if (!this.#skywayLocalRoomMember) return;

      const player = publication.publisher.name
        ? heliNetwork.findPlayerByRtcUserCode(Number.parseInt(publication.publisher.name))
        : null;
      if (!player) {
        console.debug('no player objectId', publication.publisher.name);
        return;
      }

      const skywayRoomSubscription = await this.#skywayLocalRoomMember.subscribe(publication.id);

      if (!('track' in skywayRoomSubscription.stream)) {
        throw new Error(
          'The stream property of the object returned by skywayLocalRoomMember.subscribe() does not contain a track property.'
        );
      }
      switch (skywayRoomSubscription.stream.track.kind) {
        case 'audio': {
          const audio = this.#getPlayerAudio(player).element;
          audio.autoplay = true;
          skywayRoomSubscription.stream.attach(audio);

          if (hel_isiPhone()) {
            audio.muted = true;
            if (!audio.srcObject) {
              throw new Error('player.audio.srcObject is null');
            }
            if (!(audio.srcObject instanceof MediaStream)) {
              throw new Error('player.audio.srcObject is not an instance of MediaStream');
            }
            this.setPlayerVoiceStream(player, audio.srcObject);
          } else {
            audio.play().catch((e) => {
              // 自動再生阻害時のエラーハンドリング
              const isAutoPlayBlocked =
                e.name === 'NotAllowedError' ||
                (e.message && e.message.includes("play() failed because the user didn't interact")); // Chrome/Edgeなどの詳細メッセージ
              if (isAutoPlayBlocked) {
                console.debug(`リモート音声の再生に失敗 (ユーザー操作不足): ${e.name}`, e);
                const heliNetwork = GetIHeliNetwork();
                if (heliNetwork) {
                  heliNetwork.pushPendingRemoteAudioList({player, audio});
                }
              }
            });
          }
          break;
        }
        case 'video': {
          const mediaStream = new MediaStream([skywayRoomSubscription.stream.track]);

          this.#getVideoData(player.getPlayerId()).mediaStream = mediaStream;
          this.#playScreenShareByPlayer(player);
          break;
        }
        default:
          return;
      }
    };

    this.#skywayRoom = await SkyWayRoom.FindOrCreate(skywayContext, {
      type: 'sfu',
      name: skywayChannelId
    });

    // イベント登録はjoinの前に行う
    this.#skywayRoom.onStreamPublished.add((e) => {
      subscribeAndAttach(e.publication);
    });

    // ルームに他人が参加した
    this.#skywayRoom.onMemberJoined.add((e) => {
      if (e.member.name === skywayUserId) {
        return;
      }

      const player = e.member.name
        ? heliNetwork.findPlayerByRtcUserCode(Number.parseInt(e.member.name))
        : null;
      if (!player) {
        console.error('no player objectId', e.member.name);
        return;
      }

      // 音声チャンネルへのJoinを通知
      onNetReceiveVoiceJoin(player.getPlayerId(), '');
    });

    // サブスクライブイベントを発火後
    this.#skywayRoom.onPublicationSubscribed.add((e) => {
      const publisherName = e.subscription.publication.publisher.name;
      if (publisherName === skywayUserId) {
        if (e.subscription.contentType === 'video') {
          heliNetwork.sendScreenShareStart();
        }
        return;
      }

      const player = publisherName
        ? heliNetwork.findPlayerByRtcUserCode(Number.parseInt(publisherName))
        : null;
      if (!player) {
        console.error('no player objectId', publisherName);
        return;
      }

      this.#applyPlayerVolume(player);
    });

    // 誰か退室した
    this.#skywayRoom.onMemberLeft.add((e) => {
      if (e.member.name === skywayUserId) {
        return;
      }
      const player = e.member.name
        ? heliNetwork.findPlayerByRtcUserCode(Number.parseInt(e.member.name))
        : null;
      if (!player) {
        console.debug('no player objectId', e.member.name);
        return;
      }
      // 音声チャンネルからのLeaveを通知
      onNetReceiveVoiceLeave(player.getPlayerId(), '');
    });

    this.#skywayLocalRoomMember = await this.#skywayRoom.join({
      name: skywayUserId
    });
    const positionChannelDetail = currentPosCh.getPositionChannelDetail();
    if (!positionChannelDetail) {
      throw new Error(
        'Voice RTC joining failed: The positionChannel property of the current position channel is null.'
      );
    }
    // マイクが許可されてローカルオーディオが生成されたらpublish
    const localStream = getLocalStream();
    if (localStream) {
      await this.#initLocalStream(localStream);
    }

    await heliNetwork.checkMicPermissionState();

    this.#skywayRoom.publications.forEach(subscribeAndAttach);
    onNetVoiceConnected(skywayChannelId ?? null);

    window.addEventListener('beforeunload', () => {
      hel_skyway_stop_screen_sharing_via_room();
      closeLocalStream();
      if (!this.#skywayLocalRoomMember) {
        throw new Error('skywayLocalRoomMember is null');
      }
      this.#skywayLocalRoomMember.leave();
    });

    // 既に入室しているメンバーのリストを表示
    this.#skywayRoom.members.forEach((member) => {
      if (member.name === skywayUserId) {
        return;
      }

      const player = member.name
        ? heliNetwork.findPlayerByRtcUserCode(Number.parseInt(member.name))
        : null;
      if (!player) {
        console.debug('no player objectId', member.name);
        return;
      }

      // 音声チャンネルへのJoinを通知
      onNetReceiveVoiceJoin(player.getPlayerId(), '');
    });
  }

  async #initLocalStream(localStream: MediaStream): Promise<void> {
    if (!globalThis.skyway_room) {
      throw new Error('skyway_room is not defined');
    }
    const {LocalAudioStream} = globalThis.skyway_room;

    const track = localStream.getAudioTracks()[0];
    if (!track) {
      throw new Error('getLocalStream().getAudioTracks() returns an empty array.');
    }
    const audio = new LocalAudioStream(track);
    if (!this.#skywayLocalRoomMember) {
      throw new Error('CHeliSkywayVoiceChannelProvider.#skywayLocalRoomMember is null.');
    }
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }
    const positionChannel = heliNetwork.getCurrentPositionChannel();
    if (!positionChannel) {
      throw new Error('CHeliNetwork.getCurrentPositionChannel() returns null.');
    }
    const positionChannelDetail = positionChannel.getPositionChannelDetail();
    if (!positionChannelDetail) {
      throw new Error(
        'CHeliNetwork.getCurrentPositionChannel().getPositionChannelDetail() returns null.'
      );
    }
    this.#skywayAudioPublication = await this.#skywayLocalRoomMember.publish(audio, {
      maxSubscribers: positionChannelDetail.max_voice_player_count - 1 // 自身を除いた数
    });
    if (!this.#skywayAudioPublication.stream) {
      throw new Error('skywayAudioPublication.stream is undefined');
    }
    this.#skywayAudioPublication.stream.track.enabled = false;
  }

  async onJoinPositionChannel(
    positionChannelDetail: PositionChannelDetail,
    channelSessionJwt: string
  ): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const currentPosCh = heliNetwork.getCurrentPositionChannel();

    // 座標チャンネルに接続済みか判定
    if (!currentPosCh) {
      throw new Error('getCurrentPositionChannel() returns null.');
    }

    if (currentPosCh.isMultiVc() && currentPosCh.isEnableDefaultVc()) {
      const pIHeliMic = await heliNetwork.getMicDevice();
      await this.createGenericRtcTokenAndJoinVoiceChannel(pIHeliMic, 'position_channel_code');
    }
  }

  initializePlayerVoice(player: CHeliPlayer): void {
    const element = document.createElement('audio');
    let context: AudioContext | null = null;
    let gainNode: GainNode | null = null;
    if (getUseSkywayAudioGainToggle() && hel_isiPhone()) {
      context = getMainAudioContext();
      if (context) {
        gainNode = context.createGain();
      }
    }
    this.#playerIdAudioPairs.set(player.getPlayerId(), {
      element,
      context,
      gainNode,
      source: null,
      audioDestination: null,
      attenuateVolume: 0.0
    });

    this.#applyPlayerVolume(player);
  }
  #getPlayerAudio(player: CHeliPlayer) {
    const playerId = player.getPlayerId();
    const audio = this.#playerIdAudioPairs.get(playerId);
    if (!audio) {
      throw new Error(
        `initializePlayerVoice() must be called for playerId “${playerId}” before calling #getPlayerAudio().`
      );
    }
    return audio;
  }
  onPlayerRemoved(player: CHeliPlayer): void {
    this.#playerIdScreenShareVideoDataPairs.delete(player.getPlayerId());
  }
  setPlayerVoiceStream(playerOrPlayerId: CHeliPlayer | string, stream: MediaStream): void {
    if (typeof playerOrPlayerId === 'string') {
      console.error(`setPlayerStream error : ${playerOrPlayerId}`);
      return;
    }
    // iPhoneのみGainNodeを利用する
    if (hel_isiPhone()) {
      const audio = this.#getPlayerAudio(playerOrPlayerId);
      if (audio.context) {
        audio.source = audio.context.createMediaStreamSource(stream);
        audio.audioDestination = audio.context.createMediaStreamDestination();
        console.debug('audio.audioDestination has been set');
      }
      if (audio.gainNode && audio.source && audio.audioDestination) {
        audio.source.connect(audio.gainNode);
        audio.gainNode.connect(audio.audioDestination);
        console.debug('audio.gainNode.connect has been set');
      }
      if (audio.audioDestination) {
        const newAudio = document.createElement('audio');
        newAudio.controls = true;
        newAudio.autoplay = true;
        newAudio.srcObject = audio.audioDestination.stream;
        console.debug('newAudio.srcObject has been set');
        newAudio.play().catch((e) => {
          // 自動再生阻害時のエラーハンドリング
          const isAutoPlayBlocked =
            e.name === 'NotAllowedError' ||
            (e.message && e.message.includes("play() failed because the user didn't interact")); // Chrome/Edgeなどの詳細メッセージ
          if (isAutoPlayBlocked) {
            console.debug(`リモート音声の再生に失敗 (ユーザー操作不足): ${e.name}`, e);
            const heliNetwork = GetIHeliNetwork();
            if (heliNetwork) {
              heliNetwork.pushPendingRemoteAudioList({player: playerOrPlayerId, audio: newAudio});
            }
          }
        });
      }
    }
  }
  #applyPlayerVolume(player: CHeliPlayer) {
    // 距離減衰有効無効
    if (!getUseSkywayAudioGainToggle()) {
      return;
    }
    const audio = this.#getPlayerAudio(player);
    // player個人のミュートもしくは自身の設定からミュートの場合はvolumeを0にする
    const isMuted = getMixerMuteAt(HEL_AUDIO_TYPE_VOICE);
    const mute = player.mute || isMuted ? true : false;
    const fixedVolume = this.#globalPlayerVoiceVolume * audio.attenuateVolume * (mute ? 0.0 : 1.0);
    if (hel_isiPhone()) {
      if (audio.gainNode) {
        audio.gainNode.gain.value = fixedVolume;
      }
    } else {
      audio.element.volume = fixedVolume;
    }
  }
  applyPlayerVoiceMute(player: CHeliPlayer): void {
    this.#applyPlayerVolume(player);
  }
  setPlayerVoiceAttenuate(player: CHeliPlayer, volume: number): void {
    this.#getPlayerAudio(player).attenuateVolume = volume;
    this.#applyPlayerVolume(player);
  }
  setGlobalPlayerVoiceVolume(volume: number): void {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    this.#globalPlayerVoiceVolume = volume;
    for (const player of heliNetwork.getPlayerList().values()) {
      this.#applyPlayerVolume(player);
    }
  }

  async startScreenShareStream(useCamera: boolean, useAudio: boolean): Promise<MediaStream | null> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    // ボイスチャンネルにJoinしていない
    if (!this.#skywayLocalRoomMember) {
      console.error('skyway VoiceChannel join yet');
      return null;
    }

    const currentPosCh = heliNetwork.getCurrentPositionChannel();
    if (!currentPosCh) {
      console.error('currentPosCh not connect yet.');
      return null;
    }

    if (!globalThis.skyway_room) {
      throw new Error('skyway_room is not defined');
    }
    const {SkyWayStreamFactory} = globalThis.skyway_room;
    const streams = await SkyWayStreamFactory.createDisplayStreams();
    const localVideoStream = streams.video;
    const positionChannelDetail = currentPosCh.getPositionChannelDetail();
    if (!positionChannelDetail) {
      throw new Error(
        'Screen sharing failed: The positionChannel property of the current position channel is null.'
      );
    }
    const publishOptions = {
      maxSubscribers: positionChannelDetail.max_voice_player_count - 1 // 自身を除いた数
    };
    this.#skywayVideoPublication = await this.#skywayLocalRoomMember.publish(
      localVideoStream,
      publishOptions
    );

    localVideoStream.onDestroyed.once(async () => {
      hel_skyway_stop_screen_sharing_via_room();
    });

    if (!this.#skywayVideoPublication.stream) {
      throw new Error('skywayVideoPublication.stream is undefined');
    }
    return new MediaStream([
      // MediaStreamTrackにアクセスできる
      this.#skywayVideoPublication.stream.track
    ]);
  }
  async cleanupScreenShareStream(): Promise<boolean> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    if (this.#skywayVideoPublication) {
      heliNetwork.sendData('screenshare:stop');

      // HUD上のミニビューアー表示を閉じる
      hel_video_close_genericwindow();

      // HeliScriptのコールバックを呼び出す
      hel_setText(ETextParamType_OnStopScreenShare, '');

      if (this.#skywayVideoPublication.stream) {
        this.#skywayVideoPublication.stream.track.stop();
      }
      await this.#skywayVideoPublication.cancel();
      this.#skywayVideoPublication = null;

      return true;
    } else {
      hel_video_close_genericwindow();

      // HeliScriptのコールバックを呼び出す
      hel_setText(ETextParamType_OnReceivedStopScreenShare, '');

      return false;
    }
  }
  onPlayScreenShare(): void {}
  useNotEmptyLocalStream(): boolean {
    return getLocalStream() ? true : false;
  }

  async setMicEnabled(enabled: boolean): Promise<void> {
    if (this.#skywayAudioPublication) {
      if (!this.#skywayAudioPublication.stream) {
        throw new Error('skywayAudioPublication.stream is undefined');
      }
      this.#skywayAudioPublication.stream.track.enabled = enabled;
    }
  }
  getMicEnabled(): boolean {
    return this.#skywayAudioPublication?.stream?.track.enabled ?? false;
  }
  supportsSpeakerMode(): boolean {
    return false;
  }

  setCurrentLocalTransform(param: {player: Transform; camera: Transform}): void {}

  async joinVoiceRtc(
    pIHeliMic: IHeliMic | null,
    pIHeliRtcTokenRoomTarget: IHeliRtcTokenRoomTarget | null
  ): Promise<void> {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const currentPosCh = heliNetwork.getCurrentPositionChannel();

    // 座標チャンネルに接続済みか判定
    if (!currentPosCh) {
      throw new Error('getCurrentPositionChannel() returns null.');
    }

    await this.createGenericRtcTokenAndJoinVoiceChannel(pIHeliMic, pIHeliRtcTokenRoomTarget);

    console.debug('Skyway joinVoiceRtc done');
  }
  #playScreenShareByPlayer(player: CHeliPlayer) {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    const videoData = this.#getVideoData(player.getPlayerId());
    const remoteVideoStream = videoData.mediaStream;
    if (hel_video_is_playing_same_stream(remoteVideoStream)) {
      return;
    }
    // グローバルにhel_onReceiveNoticeScreenShareStartが定義されていた場合に実行する
    if (typeof globalThis.hel_onReceiveNoticeScreenShareStart === 'function') {
      globalThis.hel_onReceiveNoticeScreenShareStart(
        player.getPlayerId(),
        remoteVideoStream,
        videoData.itemName,
        videoData.materialName
      );
    }

    // 予約
    heliNetwork.reserveRemotoVideoStream(
      remoteVideoStream,
      videoData.itemName,
      videoData.materialName
    );

    // HeliScriptのコールバックを呼び出す
    hel_setText(ETextParamType_OnReceivedStartScreenShare, '');
  }

  async initLocalMicInput(deviceId: string | null): Promise<boolean> {
    return true;
  }
  async changeMicDevice(deviceId: string): Promise<boolean> {
    if (!this.#skywayAudioPublication) {
      const localStream = getLocalStream();
      if (!localStream) {
        return true;
      }
      if (!this.#skywayLocalRoomMember) {
        // 音声チャンネルに入室していない
        return true;
      }
      await this.#initLocalStream(localStream);
      if (!this.#skywayAudioPublication) {
        return true;
      }
    }
    if (!this.#skywayAudioPublication.stream) {
      throw new Error('skywayAudioPublication.stream is undefined');
    }
    const enabled = this.#skywayAudioPublication.stream.track.enabled;
    this.#skywayAudioPublication.stream.track.stop();
    if (!globalThis.skyway_room) {
      throw new Error('skyway_room is not defined');
    }
    const {SkyWayStreamFactory} = globalThis.skyway_room;
    const audioStream = await SkyWayStreamFactory.createMicrophoneAudioStream({
      deviceId: {exact: deviceId}
    });
    this.#skywayAudioPublication.replaceStream(audioStream);

    const localMediaStream = new MediaStream([
      // MediaStreamTrackにアクセスできる
      this.#skywayAudioPublication.stream.track
    ]);
    closeLocalStream();
    setLocalStream(localMediaStream);

    this.#skywayAudioPublication.stream.track.enabled = enabled;
    if (this.getMicEnabled()) {
      // detectのmaxDomainが0になるため、止めてから再開する
      setMicEnabled(false);
      setMicEnabled(true);
    }
    return true;
  }

  setSpeakerMode(speakerMode: boolean) {
    return false;
  }

  isConnected() {
    return this.#skywayLocalRoomMember?.state === 'joined' ? true : false;
  }

  connectRoomAfterPeerOpen(): void {}
}
